import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Leaf,
  Package,
  Settings2,
  ShieldCheck,
  Shirt,
  Sparkles,
} from 'lucide-react';
import { findFeature } from './featureCatalog';
import { workspaceShortcuts } from './workspaceShortcuts';
import type { FeatureState } from './featureStatus';
import './FeatureLanding.css';

type Props = {
  featureId: string;
  state: FeatureState;
  canConfigure?: boolean;
  onPreviewDestination?: (href: string) => void;
};

export function FeatureAction({
  href,
  children,
  className = '',
  onPreviewDestination,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  onPreviewDestination?: (href: string) => void;
}) {
  const external = href.startsWith('https://');
  const content = (
    <>
      {children}
      {external ? (
        <ArrowUpRight size={16} aria-hidden="true" />
      ) : (
        <ArrowRight size={16} aria-hidden="true" />
      )}
    </>
  );
  if (external)
    return (
      <a
        className={className}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    );
  return (
    <Link
      className={className}
      to={href}
      onClick={
        onPreviewDestination
          ? (event) => {
              event.preventDefault();
              onPreviewDestination(href);
            }
          : undefined
      }
    >
      {content}
    </Link>
  );
}

function ProductIllustration({ id }: { id: string }) {
  return (
    <div className={`feature-illustration illustration-${id}`}>
      <div className="feature-art-grid" />
      <span className="feature-example-label">
        <span />
        Illustrative preview
      </span>
      {id === 'order-editing' && (
        <div className="feature-demo-card order-edit-demo">
          <div className="demo-card-top">
            <span>
              <Package size={16} />
              Order #1048
            </span>
            <span className="demo-neutral-pill">Unfulfilled</span>
          </div>
          <div className="demo-product">
            <span className="demo-product-image">
              <Shirt size={34} strokeWidth={1.2} />
            </span>
            <div>
              <strong>Everyday cotton tee</strong>
              <small>Sand / Medium</small>
            </div>
            <span className="demo-quantity">× 1</span>
          </div>
          <div className="demo-change">
            <small>UPDATE SIZE</small>
            <div>
              <span>Medium</span>
              <ArrowRight size={15} />
              <strong>
                Large <ChevronDown size={13} />
              </strong>
            </div>
          </div>
          <div className="demo-success">
            <Check size={16} />
            Your order, just right.
          </div>
          <div className="feature-floating-note">
            <Check size={14} />
            Change saved before fulfillment
          </div>
        </div>
      )}
      {id === 'returns-exchanges' && (
        <div className="feature-demo-card returns-demo">
          <div className="demo-card-top">
            <span>Find your perfect fit</span>
            <span className="demo-neutral-pill">Exchange</span>
          </div>
          <div className="demo-exchange">
            <div>
              <span className="demo-product-image">
                <Shirt size={40} strokeWidth={1.15} />
              </span>
              <small>Original · Medium</small>
            </div>
            <span className="demo-exchange-arrow">
              <ArrowRight size={20} />
            </span>
            <div>
              <span className="demo-product-image new-product">
                <Shirt size={40} strokeWidth={1.15} />
              </span>
              <small>New size · Large</small>
            </div>
          </div>
          <div className="demo-choice">
            <span>
              <RefreshIcon />
              Exchange for another size
            </span>
            <span className="demo-radio" />
          </div>
          <div className="demo-success">
            <Check size={16} />A new size. A happy customer.
          </div>
          <div className="feature-floating-note">
            <Sparkles size={14} />
            Keep the customer relationship
          </div>
        </div>
      )}
      {id === 'delivery-guarantee' && (
        <div className="feature-demo-card guarantee-demo">
          <div className="demo-card-top">
            <span>
              <ShieldCheck size={17} />
              Delivery Guarantee
            </span>
            <span className="demo-green-pill">Added</span>
          </div>
          <div className="demo-shield">
            <ShieldCheck size={52} strokeWidth={1.25} />
            <span className="demo-shield-spark">✦</span>
          </div>
          <strong className="demo-centered-title">
            A little extra peace of mind.
          </strong>
          <p className="demo-centered-copy">For the journey from us to you.</p>
          <div className="demo-timeline">
            <span className="done">
              <Check size={12} />
            </span>
            <i />
            <span className="done">
              <Package size={12} />
            </span>
            <i />
            <span>
              <Check size={12} />
            </span>
          </div>
          <div className="demo-timeline-labels">
            <span>Ordered</span>
            <span>On its way</span>
            <span>Delivered</span>
          </div>
          <div className="feature-floating-note">
            <ShieldCheck size={15} />
            Support when it matters
          </div>
        </div>
      )}
      {id === 'impact' && (
        <div className="feature-demo-card impact-demo">
          <div className="demo-card-top">
            <span>
              <Leaf size={16} />
              Small actions, lasting impact
            </span>
          </div>
          <div className="demo-tree">
            <SproutDrawing />
          </div>
          <strong className="demo-centered-title">
            Good things can grow here.
          </strong>
          <p className="demo-centered-copy">
            Bring a little purpose to the purchase.
          </p>
          <div className="demo-impact-options">
            <span>
              <Leaf size={15} />
              Tree planting
            </span>
            <span>
              <Sparkles size={15} />
              Charitable giving
            </span>
          </div>
          <div className="feature-floating-note">
            <Leaf size={14} />
            Make impact part of your story
          </div>
        </div>
      )}
      {id === 'shipping-rates' && (
        <div className="feature-demo-card rates-demo">
          <div className="demo-card-top">
            <span>
              <Package size={17} />
              Your next shipment
            </span>
            <span className="demo-neutral-pill">Service preview</span>
          </div>
          <div className="demo-route">
            <div>
              <small>FROM</small>
              <strong>Your warehouse</strong>
            </div>
            <ArrowRight size={18} />
            <div>
              <small>TO</small>
              <strong>Your customer</strong>
            </div>
          </div>
          {[
            ['Ground service', 'Everyday deliveries'],
            ['Express service', 'When timing matters'],
            ['Economy service', 'Flexible delivery'],
          ].map(([title, desc], i) => (
            <div
              className={`demo-rate ${i === 0 ? 'selected' : ''}`}
              key={title}
            >
              <span className="demo-carrier">{['G', 'E', 'S'][i]}</span>
              <span>
                <strong>{title}</strong>
                <small>{desc}</small>
              </span>
              {i === 0 ? (
                <Check size={16} />
              ) : (
                <span className="demo-empty-radio" />
              )}
            </div>
          ))}
          <div className="feature-floating-note">
            <Check size={14} />
            Choose the service that fits
          </div>
        </div>
      )}
      {id === 'fulfillment' && (
        <div className="feature-demo-card fulfillment-demo">
          <div className="demo-card-top">
            <span>
              <Package size={17} />
              From shelf to doorstep
            </span>
            <span className="demo-neutral-pill">Order flow</span>
          </div>
          <div className="demo-shelves">
            {Array.from({ length: 6 }, (_, i) => (
              <span key={i}>
                <Package size={27} strokeWidth={1.25} />
              </span>
            ))}
          </div>
          <div className="demo-fulfillment-flow">
            {['Received', 'Picked', 'Packed'].map((step, i) => (
              <React.Fragment key={step}>
                <div>
                  <span>
                    <Check size={14} />
                  </span>
                  <small>{step}</small>
                </div>
                {i < 2 && <i />}
              </React.Fragment>
            ))}
          </div>
          <div className="demo-success">
            <Package size={16} />
            Ready for the next step.
          </div>
          <div className="feature-floating-note">
            <Check size={14} />
            Your operations, connected
          </div>
        </div>
      )}
    </div>
  );
}

function RefreshIcon() {
  return <span aria-hidden="true">↔</span>;
}
function SproutDrawing() {
  return (
    <svg
      width="130"
      height="120"
      viewBox="0 0 130 120"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M65 108V55"
        stroke="#3c8060"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path d="M65 73C31 75 22 52 24 31C50 29 68 44 65 73Z" fill="#9bc7a3" />
      <path d="M65 60C63 24 83 11 107 15C110 42 94 61 65 60Z" fill="#5a9e73" />
      <path
        d="M65 75L39 49M65 60L88 35"
        stroke="#3c8060"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <ellipse cx="65" cy="109" rx="34" ry="6" fill="#dcebdc" />
    </svg>
  );
}

export function FeatureLanding({
  featureId,
  state,
  canConfigure = true,
  onPreviewDestination,
}: Props) {
  const feature = findFeature(featureId);
  if (!feature)
    return (
      <div className="feature-landing">
        <h1>Feature not found</h1>
        <Link to="/features/delivery-guarantee">
          Explore Delivery Guarantee
        </Link>
      </div>
    );
  const active = state === 'active';
  const pending = state === 'pending';
  const external = !feature.settings;
  const status =
    state === 'loading'
      ? 'Checking status'
      : active
      ? 'Active'
      : pending
      ? featureId === 'shipping-rates'
        ? 'Application in progress'
        : 'Setup in progress'
      : state === 'unknown'
      ? external
        ? 'Status not connected'
        : featureId === 'order-editing'
        ? 'Setup not verified'
        : 'Status unavailable'
      : 'Not enabled';
  const primary = active
    ? feature.launch
    : pending && featureId === 'shipping-rates'
    ? { label: 'Continue rate application', href: feature.setup.href }
    : pending
    ? { ...feature.setup, label: 'Continue setup' }
    : feature.setup;
  const primaryAllowed =
    canConfigure ||
    (active &&
      featureId !== 'order-editing' &&
      !primary.href.startsWith('/settings') &&
      !primary.href.startsWith('/order-settings'));
  const secondary =
    active && external
      ? featureId === 'shipping-rates'
        ? { label: 'View rate application', href: 'https://apply.shipaid.com' }
        : null
      : !active && feature.secondary
      ? feature.secondary
      : canConfigure && feature.settings
      ? { label: 'Manage settings', href: feature.settings }
      : null;
  const heroFootnote = (() => {
    if (featureId === 'fulfillment' && !active && primaryAllowed) {
      return 'Schedule your fulfillment audit in a new tab';
    }
    if (external) {
      return 'Your dedicated SHIPAID portal opens in a new tab';
    }
    if (active) {
      return 'Connected to your SHIPAID workflow';
    }
    return 'Set up and manage everything from your account';
  })();
  const showCustomerPortalShortcut =
    featureId === 'returns-exchanges' || featureId === 'delivery-guarantee';
  const Icon = feature.icon;
  return (
    <div className={`feature-landing feature-${feature.id}`}>
      <div className="feature-breadcrumb">
        <span>Products</span>
        <ChevronRight size={13} aria-hidden="true" />
        <span>{feature.name}</span>
      </div>
      <header className="feature-page-header">
        <div className="feature-page-title">
          <span className="feature-product-icon">
            <Icon size={22} />
          </span>
          <h1>{feature.name}</h1>
        </div>
        <div className="feature-page-actions">
          {showCustomerPortalShortcut && (
            <FeatureAction
              href="https://resolve.shipaid.com/"
              className="feature-secondary-link"
            >
              Open customer portal
            </FeatureAction>
          )}
          <span
            role="status"
            className={`feature-status feature-status-${state}`}
          >
            <span />
            {status}
          </span>
        </div>
      </header>
      <div className="feature-overview-label">
        Overview<span>Made for your next move</span>
      </div>
      {(state === 'unknown' || state === 'loading') && (
        <div className="feature-state-note">
          {state === 'loading'
            ? 'Checking the selected store’s current setup…'
            : external
            ? `Your ${
                featureId === 'shipping-rates'
                  ? 'rate approval and shipping'
                  : 'fulfillment'
              } account status isn’t available in this app yet. Already set up? Open your portal below.`
            : 'We can’t confirm the current setup from the information available. Review settings before making changes.'}
        </div>
      )}
      <section
        className={`feature-hero ${active ? 'feature-hero-active' : ''}`}
      >
        <div className="feature-hero-copy">
          <span className="feature-eyebrow">
            {active ? 'YOUR WORKSPACE' : feature.category}
          </span>
          <h2>{active ? feature.activeHeadline : feature.headline}</h2>
          <p>{active ? feature.activeDescription : feature.description}</p>
          <div className="feature-hero-actions">
            {primaryAllowed ? (
              <FeatureAction
                href={primary.href}
                className="feature-button feature-button-primary"
                onPreviewDestination={onPreviewDestination}
              >
                {primary.label}
              </FeatureAction>
            ) : (
              <p className="feature-permission-note">
                Ask a store admin or manager to complete setup.
              </p>
            )}
            {secondary && (canConfigure || (!active && external)) && (
              <FeatureAction
                href={secondary.href}
                className="feature-secondary-link"
                onPreviewDestination={onPreviewDestination}
              >
                {secondary.label}
              </FeatureAction>
            )}
          </div>
          <div className="feature-hero-footnote">
            <Check size={14} />
            {heroFootnote}
          </div>
        </div>
        <ProductIllustration id={feature.id} />
      </section>
      {active &&
      workspaceShortcuts[featureId]?.some(
        (shortcut) => canConfigure || !shortcut.configure,
      ) ? (
        <section
          className="feature-workspace-shortcuts"
          aria-label={
            featureId === 'returns-exchanges'
              ? 'Your returns shortcuts'
              : `Your ${feature.name.toLowerCase()} shortcuts`
          }
        >
          <div className="feature-shortcuts-header">
            <h2>Jump back into work</h2>
            <span>Your next step, a little closer.</span>
          </div>
          <div className="feature-shortcut-grid">
            {workspaceShortcuts[featureId]
              .filter((shortcut) => canConfigure || !shortcut.configure)
              .map((shortcut) => (
                <FeatureAction
                  href={shortcut.href}
                  key={shortcut.label}
                  className="feature-shortcut"
                  onPreviewDestination={onPreviewDestination}
                >
                  <span>
                    <strong>{shortcut.label}</strong>
                    <small>{shortcut.description}</small>
                  </span>
                </FeatureAction>
              ))}
          </div>
        </section>
      ) : (
        <section
          className="feature-benefits"
          aria-label={`${feature.name} capabilities`}
        >
          {feature.benefits.map((benefit, index) => {
            const BenefitIcon =
              [feature.icon, Settings2, Sparkles][index] || Sparkles;
            return (
              <article key={benefit.title}>
                <span className="feature-benefit-icon">
                  <BenefitIcon size={20} strokeWidth={1.6} />
                </span>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </article>
            );
          })}
        </section>
      )}
      <section className="feature-guide">
        <div className="feature-guide-intro">
          <span className="feature-eyebrow">
            {active ? 'KEEP THINGS RUNNING' : 'A CLEAR PATH FORWARD'}
          </span>
          <h2>
            {active
              ? 'Make the most of it.'
              : pending
              ? 'Pick up where you left off.'
              : 'From here to your first order.'}
          </h2>
          <p>
            {active
              ? 'A few useful places to review as your business grows.'
              : 'A few simple steps to get your store ready.'}
          </p>
          <a
            href="https://help.shipaid.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <CircleHelp size={16} />
            Visit the help center
            <ArrowUpRight size={14} />
          </a>
        </div>
        <div className="feature-guide-steps">
          {feature.steps.map((step, index) => (
            <details key={step.title}>
              <summary>
                <span className="feature-step-number">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span>{step.title}</span>
                <ChevronDown size={16} />
              </summary>
              <p>{step.description}</p>
            </details>
          ))}
        </div>
      </section>
      <footer className="feature-page-footer">
        <span>
          <ShieldCheck size={14} />
          Part of your SHIPAID toolkit
        </span>
        <span>One connected customer journey.</span>
      </footer>
    </div>
  );
}
